import React, {Suspense, useCallback, useEffect, useMemo, useState} from "react";
import './shim';
// Web/desktop only: installs the Tauri V2 Electron-compat globals (ipcRenderer,
// appStorage, startMQTTService, …) before React mounts. Resolves to a no-op on
// native (bootTauri.js) so @tauri-apps/* never enters the Metro/Hermes bundle.
import './app/tauri/bootTauri';
import {AppState, BackHandler, Linking, Platform} from "react-native";
import Toast from 'react-native-toast-message';
import {activateKeepAwakeAsync} from 'expo-keep-awake';
import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';
import Observables from '@happy-gastro/global-observables';
import {persistor, store} from "./app/store";
// Guarded require: a stale native binary missing the compiled ExpoFontLoader
// TurboModule would otherwise throw TurboModuleRegistry.getEnforcing / a
// "Cannot find native module 'ExpoFontLoader'" error at IMPORT time (before
// registerRootComponent/AppRegistry.registerComponent ever runs), aborting
// the whole app boot with "Invariant Violation: main has not been
// registered". Degrade to a no-op Font stub instead — same pattern as the
// react-native-maps guard in app/screens/OnlineMapScreen.native.js.
let Font = null;
try {
    Font = require("expo-font");
} catch (_) {
    // native module not compiled into this binary — Font stays null
}
import AsyncStorage from "@react-native-async-storage/async-storage";
import {getAccessToken, getUserInfo} from "./app/helpers/storage";
import {useTranslation} from "react-i18next";
import i18n from "./i18n";
import {validateSessionOrLogout} from './app/helpers/logout';
import {registerBackgroundTasks} from './app/helpers/BackgroundTasks';
import {registerMailNotifications} from './app/helpers/mailNotifications';
import {registerQueueProcessingTask} from './app/helpers/OfflineQueue';
import ToastConfig from "./app/helpers/ToastConfig";
import ErrorReportingService from './app/helpers/ErrorReportingService';
import {getSetting, getSettingJSON, setSetting} from './app/helpers/persistentSettings';
import {
    getAvailableAppModesForUser,
    getDefaultOperationalModeForUser,
} from './app/helpers/managerRoles';
import {resolveModeForAppVariant} from './app/config/appVariant';
import {hydrateDemoMode, installDemoHttpProxy} from './app/demo';
import {settleAll} from './app/helpers/promisePolyfills';
import refreshWidgets, {isWidgetRelevantPush, registerWidgetBackgroundRefresh} from './app/helpers/widgets/widgetRefresh';
import {addForegroundMessageListener} from './app/services/pushNotifications';
import {handleWidgetUrl} from './app/helpers/widgets/widgetDeepLink';
import WidgetChartHost from './app/components/widgets/WidgetChartHost';

// Import our custom components
import AppProviders from './app/components/AppProviders';
import ThemedStatusBar from './app/components/ThemedStatusBar';
import AppNavigator from './app/components/AppNavigator';
import OrderNotificationService from './app/helpers/OrderNotificationService';
import CallProvider from './app/helpers/CallProvider';
import IncomingCallBanner from './app/components/voip/IncomingCallBanner';
import DriverPushBanner from './app/components/driver/DriverPushBanner';
import SplashScreenWrapper from './app/components/SplashScreenWrapper';
import BootLoader from './app/components/BootLoader';
import CashRegisterRetryDialog from './app/components/CashRegisterRetryDialog';
import CashRegisterQRModal from './app/components/CashRegisterQRModal';
import WhatsNewModal from './app/components/WhatsNewModal';
import {WorkdayProvider} from './app/helpers/WorkdayContext';
import WorkLocationHost from './app/components/workLocation/WorkLocationHost';
import ShareIntentHost from './app/components/share/ShareIntentHost';
import WorkdayOverlay from './app/components/workday/WorkdayOverlay';
import AppUpdateModal from './app/components/AppUpdateModal';
import {installAlertShim} from './app/components/HappyAlert';

// Route every Alert.alert(...) call across the app through the premium HappyAlert modal.
installAlertShim();

// Demo mode: patch the transports (global.fetch + axios) ONCE at boot so PII
// redaction reaches every screen — including the many detail screens and
// app/services/* modules that fetch directly instead of through a shared client.
// The wrappers no-op unless demo mode is on, so installing unconditionally is safe.
installDemoHttpProxy();

if (Platform.OS === 'web') {
    require('./happyWebStyles.css');
}

// Define background task
const BACKGROUND_FETCH_TASK = 'happy-services-sync';
TaskManager.defineTask(BACKGROUND_FETCH_TASK, async () => {
    try {
        // Perform your background task here
        return { type: 'BACKGROUND_TASK', action: BACKGROUND_FETCH_TASK };
    } catch (_error) {
        return false;
    }
});

// Hard upper bound on the cold-start boot window. If the boot batches (runtime
// state + font decode) have not settled within this many milliseconds, the app
// is released anyway so the user is never trapped on the loading/splash screen
// by a single hung native promise. See the Promise.race watchdog in loadFont().
const BOOT_MAX_MS = 8000;

// Loading fallback component — the premium boot screen (brand gradient, logo
// plate, progress bar). Self-contained: it renders before any provider mounts.
const LoadingFallback = ({ message }) => <BootLoader message={message} />;

// Memoize the LoadingFallback component to prevent unnecessary re-renders
const MemoizedLoadingFallback = React.memo(LoadingFallback);

export default function App({ route }) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [appIsReady, setAppIsReady] = useState(false);
    const [isDayOpen, setIsDayOpen] = useState(false);
    const [isServiceMode, setIsServiceMode] = useState(false);
    const [isStockMode, setIsStockMode] = useState(false);
    const [isManagerMode, setIsManagerMode] = useState(false);
    const [isSalesMode, setIsSalesMode] = useState(false);
    const [isDriverMode, setIsDriverMode] = useState(false);
    const [currentRestaurant, setCurrentRestaurant] = useState(null);
    const { t } = useTranslation();
    const parseDayOpenedValue = useCallback((storedValue) => storedValue === 'true', []);

    const toastConfig = useMemo(() => {
        if (!isStockMode && !isDriverMode) {
            return ToastConfig;
        }

        return {
            ...ToastConfig,
            pendingOrderToast2: () => null,
        };
    }, [isStockMode, isDriverMode]);

    const loadRuntimeState = useCallback(async () => {
        setIsAuthReady(false);
        const [accessToken, storedUserAppMode, storedDayOpened, restaurant, userInfo, restaurantPlace] = await Promise.all([
            getAccessToken(),
            getSetting('userAppMode'),
            getSetting('dayOpened'),
            getSettingJSON('restaurant', null),
            getUserInfo(),
            getSettingJSON('restaurantPlace', null),
        ]);

        const allowedModes = getAvailableAppModesForUser(userInfo, restaurantPlace);

        const roleBasedDefaultMode = getDefaultOperationalModeForUser(userInfo, restaurantPlace);
        const userMode = allowedModes.includes(storedUserAppMode) ? storedUserAppMode : roleBasedDefaultMode;
        const resolvedUserAppMode = resolveModeForAppVariant(userMode, roleBasedDefaultMode);

        if (storedUserAppMode !== resolvedUserAppMode) {
            // Fire-and-forget: do NOT block boot on the persistence write. On iOS
            // this maps to a Keychain write (expo-secure-store) which, on a fresh
            // install / locked-keychain review sandbox, can stall — and this call
            // sits on the critical path to setAppIsReady(true). The in-memory
            // `resolvedUserAppMode` already drives the app-mode flags below, so the
            // write only needs to persist for the NEXT launch, not this one.
            setSetting('userAppMode', resolvedUserAppMode).catch(() => {});
        }

        setIsLoggedIn(Boolean(accessToken));
        setIsServiceMode(resolvedUserAppMode === 'service');
        setIsStockMode(resolvedUserAppMode === 'stock');
        setIsManagerMode(resolvedUserAppMode === 'manager');
        setIsSalesMode(resolvedUserAppMode === 'sales');
        setIsDriverMode(resolvedUserAppMode === 'driver');
        setIsDayOpen(parseDayOpenedValue(storedDayOpened));
        setCurrentRestaurant(restaurant);
        setIsAuthReady(true);
    }, [parseDayOpenedValue]);

    // Memoize the error handler function to prevent recreation on each render
    const errorHandler = useCallback(async (error, stacktrace) => {
        return ErrorReportingService.reportBoundaryError(error, stacktrace, {
            sourceComponent: 'AppOptimizedRootErrorBoundary',
            routeName: route?.name,
        });
    }, [route?.name]);

    // Memoize the openAdmin function to prevent recreation on each render
    const openAdmin = useCallback(async () => {
        // Pass the JWT in the URL fragment (#token=) — fragments are never sent
        // to the server, stored in access logs, or leaked via the Referer header
        // the way a ?token= query string is (see QA MGMT-027).
        const link = 'https://admin.happygastro.org/#token=' + await getAccessToken();
        Linking.canOpenURL(link)
            .then((supported) => {
                if (supported) {
                    Linking.openURL(link);
                } else {
                    console.log("Don't know how to open URI: " + link);
                }
            })
            .catch((err) => console.error('An error occurred', err));
    }, []);

    // Initialize language
    useEffect(() => {
        const getSavedLanguage = async () => {
            const savedLanguage = await AsyncStorage.getItem('selectedLanguage');
            return savedLanguage || 'en';
        };
        
        const initializeLanguage = async () => {
            const language = await getSavedLanguage();
            i18n.changeLanguage(language);
        };
        
        initializeLanguage();
    }, []);

    // Initialize app data and keep screen awake
    useEffect(() => {
        const keepAwake = async () => {
            await activateKeepAwakeAsync();
        };
        
        // Register for day opening/closing events
        Observables.register('reloadDayOpeningClosing', async () => {
            await loadRuntimeState();

            // Force app restart on Android
            if (Platform.OS === 'android') {
                BackHandler.exitApp();
            } else if (Platform.OS === 'web') {
                window.location.reload(true);
            }
        });

        // Soft app-mode switch (Settings → application selector): re-resolve the
        // active mode flags from the freshly persisted `userAppMode` WITHOUT a
        // full page reload/app restart, so the navigator shows the chosen app.
        Observables.register('reloadAppMode', async () => {
            await loadRuntimeState();
        });

        // Boot the app. CRITICAL: this must ALWAYS reach setAppIsReady(true),
        // otherwise the user is stuck forever on the loading screen with no
        // splash animation and no way into the app (the exact iOS hang we hit).
        // On iOS a single rejected promise here (a font that fails to decode,
        // activateKeepAwakeAsync throwing, an AsyncStorage read rejecting) used
        // to reject loadFont() and silently abort boot. Now every stage is
        // isolated with allSettled / try-catch and the app is released no
        // matter what.
        const loadFont = async () => {
            // Kick off BOTH independent boot batches up front so runtime-state
            // disk I/O overlaps with native font decode instead of running
            // strictly after it. The batches are independent (fonts don't read
            // runtime state and vice-versa), so overlapping them only shortens the
            // splash window on low-end Android. setAppIsReady(true) still fires
            // ONLY after both settle, so first render sees identical, fully
            // resolved state — same ordering guarantee, less wall-clock time.
            const runtimeSettled = settleAll([
                loadRuntimeState(),
                keepAwake(),
                // Prime the Demo-mode flag BEFORE the first data fetch so the
                // very first response is already redacted when it's on.
                hydrateDemoMode(),
            ]).catch((error) => {
                console.error('[Boot] runtime state init failed, continuing anyway:', error);
            });

            try {
                // Font stays null if the native ExpoFontLoader module wasn't
                // compiled into this binary (see the guarded require above) —
                // skip preloading entirely rather than crash on Font.loadAsync.
                if (Font && typeof Font.loadAsync === 'function') {
                    const fontPromises = [
                        Font.loadAsync("MaterialIcons", require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialIcons.ttf")),
                        Font.loadAsync("FontAwesome", require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/FontAwesome.ttf")),
                        Font.loadAsync("FontAwesome5", require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/FontAwesome5_Regular.ttf")),
                        Font.loadAsync("Ionicons", require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Ionicons.ttf")),
                        Font.loadAsync("MaterialCommunityIcons", require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf")),
                        Font.loadAsync("Feather", require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Feather.ttf"))
                    ];

                    // Fonts are intentionally OFF the release gate. The six icon TTFs
                    // (~2.1 MB, MaterialCommunityIcons alone ~1.15 MB) used to block
                    // first paint, yet the loading/splash surface renders NO icons.
                    // SplashScreenWrapper shows the Lottie splash before any
                    // icon-bearing screen mounts, so the fonts get a full background
                    // decode window during the splash — fire-and-forget here.
                    // settleAll (not all) so a single corrupt glyph font can't reject.
                    settleAll(fontPromises);
                } else {
                    console.warn('[Boot] expo-font native module unavailable, skipping icon font preload');
                }
            } catch (error) {
                console.error('[Boot] font preload failed, continuing anyway:', error);
            }

            // Release the app as soon as RUNTIME STATE is ready (fonts decode in
            // the background). Bound the wait with a hard watchdog: settleAll
            // never resolves if an input promise never SETTLES (a hung native
            // call — an iOS Keychain read or a stalled disk read that neither
            // resolves nor rejects). The race guarantees setAppIsReady(true)
            // within BOOT_MAX_MS no matter what; a timer can only ADVANCE boot.
            await Promise.race([
                runtimeSettled,
                new Promise((resolve) => globalThis.setTimeout(resolve, BOOT_MAX_MS)),
            ]);

            setAppIsReady(true);
        };

        loadFont().catch((error) => {
            // Last-resort guard: even if loadFont itself blows up synchronously,
            // release the app so the user is never trapped on the splash.
            console.error('[Boot] loadFont crashed, releasing app anyway:', error);
            setAppIsReady(true);
        });
        
        // Cleanup function to unregister observers. Use `remove` (the actual
        // library method — there is no `unregister`) so that a full restart
        // (root remount via `restartApp`) properly drops the old handlers and
        // the fresh mount can re-register callbacks bound to current state.
        return () => {
            Observables.remove('reloadDayOpeningClosing');
            Observables.remove('reloadAppMode');
        };
    }, [loadRuntimeState]);

    useEffect(() => {
        const cleanupGlobalHandlers = ErrorReportingService.installGlobalHandlers({
            sourceComponent: 'AppOptimizedRoot',
        });

        return () => {
            cleanupGlobalHandlers?.();
        };
    }, []);

    // Initialize background tasks
    useEffect(() => {
        // expo-background-fetch / expo-task-manager have no web implementation
        // (`BackgroundFetch.registerTaskAsync is not available on web`). Skip
        // the entire registration flow on web to avoid the noisy failure.
        if (Platform.OS === 'web') {
            return;
        }

        // Defer registration until AFTER first paint (appIsReady). These are
        // 15-minute OS background-fetch/poll tasks, so registering them a moment
        // later is not user-observable, and it keeps their first module
        // evaluation off the cold-start critical path. Runs exactly once
        // (appIsReady only transitions false→true) and registration is idempotent.
        if (!appIsReady) {
            return;
        }

        const initBackgroundFetch = async () => {
            try {
                // Register the app's main background fetch task
                await BackgroundFetch.registerTaskAsync(BACKGROUND_FETCH_TASK, {
                    minimumInterval: 15, // Fetch interval in minutes
                    stopOnTerminate: false, // Continue running after app is terminated
                    startOnBoot: true, // Start task when device is booted
                });

                // Register our custom background tasks for API requests and heavy calculations
                await registerBackgroundTasks();

                // Register the mail-poll task that raises local new-mail
                // notifications (fallback for when FCM push is unavailable).
                await registerMailNotifications();

                // Register offline queue processing task
                await registerQueueProcessingTask();

                // Keep the home-screen widgets warm in the background (~30 min).
                await registerWidgetBackgroundRefresh();

                console.log('Background tasks registered successfully');
            } catch (error) {
                console.error('Failed to register background tasks:', error);
            }
        };
        
        initBackgroundFetch();
    }, [appIsReady]);

    // NOTE: a former "check day open status when route changes" effect that
    // re-ran loadRuntimeState() on mount was removed — `route` is always
    // undefined at the root component (App is registered via
    // registerRootComponent with no route prop), so it never fired for its
    // stated purpose and only duplicated boot's own loadRuntimeState() call
    // (6 redundant storage reads + a double isAuthReady churn at cold start).
    // Day-open / app-mode refreshes are handled by the Observables
    // 'reloadDayOpeningClosing' / 'reloadAppMode' registrations above.

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        // Verify the stored token still maps to a real user. If `/me` comes back
        // with anything that is not a valid user object (revoked/expired token,
        // deleted account, error body) we log the device out fully and return to
        // the Login screen. Runs AFTER boot and is fully non-blocking so it can
        // never re-introduce a splash hang.
        validateSessionOrLogout().catch(() => {
            // Never let a validation failure crash the app — offline/transient
            // errors intentionally leave the session untouched.
        });
    }, [isLoggedIn]);

    // Runtime session/access heartbeat: re-validate on every return to the
    // foreground and on a throttled interval, so an expired session, a remotely
    // signed-out device, a disabled account or a revoked restaurant kicks the
    // user back to Login instead of leaving them on a stale screen.
    // validateSessionOrLogout is defensive (only a definitive 401/403 or revoked
    // access logs out; transient/offline errors keep the session), so running it
    // periodically is safe. Throttled so a focus + interval overlap is harmless.
    useEffect(() => {
        if (!isLoggedIn) {
            return undefined;
        }

        const THROTTLE_MS = 30 * 1000;
        const INTERVAL_MS = 2 * 60 * 1000;
        let lastCheckAt = 0;

        const runSessionCheck = () => {
            const now = Date.now();

            if (now - lastCheckAt < THROTTLE_MS) {
                return;
            }
            lastCheckAt = now;
            validateSessionOrLogout().catch(() => {
                // Transient/offline errors intentionally keep the session.
            });
        };

        const appStateSub = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
                runSessionCheck();
            }
        });
        const intervalId = setInterval(runSessionCheck, INTERVAL_MS);

        return () => {
            appStateSub?.remove?.();
            clearInterval(intervalId);
        };
    }, [isLoggedIn]);

    // Home-screen widgets: publish a fresh snapshot when the user logs in and
    // whenever the app returns to the foreground, and route widget taps
    // (happy-app://widget/...) to the matching screen. All best-effort — a widget
    // failure must never affect the app.
    useEffect(() => {
        if (Platform.OS === 'web' || !isLoggedIn) {
            return undefined;
        }

        refreshWidgets();

        const appStateSub = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
                refreshWidgets();
            }
        });

        const linkSub = Linking.addEventListener('url', ({ url }) => handleWidgetUrl(url));
        Linking.getInitialURL()
            .then((url) => {
                if (url) {
                    handleWidgetUrl(url);
                }
            })
            .catch(() => {});

        return () => {
            appStateSub?.remove?.();
            linkSub?.remove?.();
        };
    }, [isLoggedIn]);

    // Push-driven widget sync (foreground twin of the handler in index.js).
    // The backend fires a silent, data-only `widget.refresh` push
    // (widgetSync.service#notifyWidgetTimerChanged) whenever the time tracker is
    // started/paused/resumed/stopped anywhere — the admin web panel included —
    // so the home-screen widget repaints within seconds instead of waiting for
    // the ~30-minute background fetch. The message carries no notification block
    // and must never raise a banner, so this branch only refreshes and returns.
    useEffect(() => {
        if (Platform.OS === 'web' || !isLoggedIn) {
            return undefined;
        }

        let unsubscribe = null;

        try {
            unsubscribe = addForegroundMessageListener(async (message) => {
                if (message?.data?.type !== 'widget.refresh') {
                    // Chat / meeting / call pushes keep their normal banner
                    // handling elsewhere; they only nudge the collaboration
                    // widgets here (fire-and-forget, coalesced).
                    if (isWidgetRelevantPush(message?.data)) {
                        refreshWidgets().catch(() => {});
                    }

                    return;
                }

                try {
                    await refreshWidgets();
                } catch (_error) {
                    // A widget failure must never affect the app.
                }
            });
        } catch (_error) {
            // FCM unavailable (no Play Services / simulator) — the pull-based
            // AppState + background-fetch refreshes still keep widgets warm.
        }

        return () => {
            try {
                unsubscribe?.();
            } catch (_error) {
                // Already torn down — ignore.
            }
        };
    }, [isLoggedIn]);

    // If app is not ready, show loading screen
    if (!appIsReady) {
        return <MemoizedLoadingFallback message={t('loading')} />;
    }

    return (
        <SplashScreenWrapper initialVisible={true} route={route}>
            <AppProviders 
                store={store} 
                persistor={persistor} 
                errorHandler={errorHandler}
            >
                <Suspense fallback={<MemoizedLoadingFallback message={t('loading')} />}>
                    <ThemedStatusBar />
                    {/* Munkanap (workday clock-in/out): ONE provider above the navigator so every module's header
                        pill, the briefing and the global WorkdayOverlay share the same state. Fail-soft: it renders
                        nothing when the backend does not support /workday yet. */}
                    <WorkdayProvider isLoggedIn={isLoggedIn}>
                    {/* CallProvider hosts the foreground softphone (SIP/WebRTC) and the
                        floating incoming-call banner / active-call bar overlays every
                        screen. Guarded natively — a no-op until the WebRTC pod ships. */}
                    <CallProvider isLoggedIn={isLoggedIn}>
                        <AppNavigator
                            isLoggedIn={isLoggedIn}
                            isServiceMode={isServiceMode}
                            isStockMode={isStockMode}
                            isManagerMode={isManagerMode}
                            isSalesMode={isSalesMode}
                            isDriverMode={isDriverMode}
                            isDayOpen={isDayOpen}
                            currentRestaurant={currentRestaurant}
                            openAdmin={openAdmin}
                        />
                        <IncomingCallBanner />
                        {/* Driver app: large foreground banner for route / stock / change-request pushes. */}
                        {isDriverMode && <DriverPushBanner />}
                        {/*
                            Global incoming-order watcher: subscribes to the live
                            OrdersContext `orders` (Socket.IO `order:create` + 10s
                            polling floor) and, the instant a new external/online
                            delivery order lands in `pending`, auto-pops the
                            NewOrdersNotificationModal and loops the alert sound —
                            from ANY screen, including SaleScreen (POS sales mode),
                            which otherwise only reflected new orders in the tiny
                            header bell badge. Gated to order-taking modes: service
                            technicians (`service`), stock (`stock`) users and field
                            sales reps (`sales`) and drivers (`driver`) don't handle delivery orders, so they
                            never get the popup/sound.
                        */}
                        {!isServiceMode && !isStockMode && !isSalesMode && !isDriverMode && <OrderNotificationService />}
                    </CallProvider>
                    <WhatsNewModal isLoggedIn={isLoggedIn} isAuthReady={isAuthReady} />
                    {/* Workday status sheet / end-of-day reminder / start sheet — one surface at a time, over every module. */}
                    <WorkdayOverlay isLoggedIn={isLoggedIn} />
                    {/* Work-event location pings: one-time disclosure sheet + throttled app-open ping + offline queue flush. */}
                    <WorkLocationHost isLoggedIn={isLoggedIn} />
                    </WorkdayProvider>
                    {/* "Share to Happy": receives text / links / photos / files from the OS share sheet and
                        opens ShareToHappyScreen once logged in. Native only; renders nothing elsewhere. */}
                    <ShareIntentHost isLoggedIn={isLoggedIn} />
                    <AppUpdateModal />
                    <CashRegisterRetryDialog />
                    <CashRegisterQRModal />
                    {Platform.OS !== 'web' ? <WidgetChartHost /> : null}
                    <Toast config={toastConfig} />
                </Suspense>
            </AppProviders>
        </SplashScreenWrapper>
    );
}